// src/Pages/Writers.js
import React, { useEffect, useMemo, useState } from 'react';
import pencilIcon from '../assets/icons/pencil.svg';
import { api } from '../api';

export default function Writers() {
  // Full dataset so search can hit everything
  const [allWriters, setAllWriters] = useState([]);

  // Paging (like Artists)
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Search (style & placement identical to Artists)
  const [search, setSearch] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingWriter, setEditingWriter] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [pro, setPro] = useState('');
  const [ipi, setIpi] = useState('');
  const [publisher, setPublisher] = useState('');

  useEffect(() => {
    fetchWriters();
  }, []);

  async function fetchWriters() {
    try {
      const res = await api('/api/writers');
      const data = await res.json();
      setAllWriters(Array.isArray(data) ? data : []);
      setVisibleCount(PAGE_SIZE); // reset page on refetch
    } catch (err) {
      console.error('Failed to fetch writers:', err);
      setAllWriters([]);
    }
  }

  // Search across ALL writers
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allWriters;
    return allWriters.filter((w) => (w?.name || '').toLowerCase().includes(q));
  }, [allWriters, search]);

  // Visible slice
  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  // Reset to first page whenever search changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search]);

  function openAddModal() {
    setEditingWriter(null);
    setName('');
    setPro('');
    setIpi('');
    setPublisher('');
    setShowModal(true);
  }

  function openEditModal(writer) {
    setEditingWriter(writer);
    setName(writer.name || '');
    setPro(writer.pro || '');
    setIpi(writer.ipi || '');
    setPublisher(writer.publisher || '');
    setShowModal(true);
  }

  async function saveWriter() {
    if (!name.trim()) {
      alert('Writer Name is required.');
      return;
    }
    const payload = {
      name: name.trim(),
      pro: pro.trim(),
      ipi: ipi.trim(),
      publisher: publisher.trim(),
    };

    try {
      if (editingWriter) {
        const res = await api(`/api/writers/${editingWriter._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Save failed (${res.status})`);
      } else {
        const res = await api('/api/writers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Create failed (${res.status})`);
      }
      await fetchWriters();
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to save writer');
    }
  }

  async function deleteWriter() {
    if (!editingWriter) return;
    const confirmed = window.confirm(
      `Delete writer “${editingWriter.name || 'Unnamed'}”?`
    );
    if (!confirmed) return;

    try {
      const res = await api(`/api/writers/${editingWriter._id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await fetchWriters();
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete writer');
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h1 className="header" style={{ marginTop: 10, marginBottom: 12, color: '#fff' }}>
        Writers
      </h1>

      {/* Create button (unchanged, same style & width as your Artists page) */}
      <button
        onClick={openAddModal}
        style={{
          backgroundColor: '#1e1e1e',
          color: 'white',
          border: 'none',
          padding: '15px 30px',
          borderRadius: 8,
          cursor: 'pointer',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'bold',
        }}
      >
        <span style={{ fontSize: 24, marginRight: 10 }}>+</span>
        Create New Writer
      </button>

      {/* Search (exact same placement + width rules as Artists) */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search writers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: 300,      // match Artists page search width
            padding: 10,
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: 420,
              maxWidth: '90vw',
              padding: 20,
              backgroundColor: '#1e1e1e',
              color: 'white',
              borderRadius: 10,
              margin: 'auto',
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingWriter ? 'Edit Writer' : 'Add New Writer'}
            </h2>

            {/* Writer Name */}
            <label style={{ display: 'block', marginBottom: 6 }}>Writer Name</label>
            <input
              type="text"
              placeholder="e.g., John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 12,
                padding: 10,
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* PRO */}
            <label style={{ display: 'block', marginBottom: 6 }}>PRO</label>
            <input
              type="text"
              placeholder="e.g., ASCAP, BMI, PRS, GEMA…"
              value={pro}
              onChange={(e) => setPro(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 12,
                padding: 10,
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* IPI Number */}
            <label style={{ display: 'block', marginBottom: 6 }}>IPI Number</label>
            <input
              type="text"
              placeholder="e.g., 123456789"
              value={ipi}
              onChange={(e) => setIpi(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 12,
                padding: 10,
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* Publisher */}
            <label style={{ display: 'block', marginBottom: 6 }}>Publisher</label>
            <input
              type="text"
              placeholder="e.g., CloudKid Publishing"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 12,
                padding: 10,
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: 5,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* Buttons Row */}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={saveWriter}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: 5,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: 5,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>

              {editingWriter && (
                <button
                  onClick={deleteWriter}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#ea5a54',
                    color: 'white',
                    border: 'none',
                    borderRadius: 5,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    marginLeft: 'auto',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Writers Grid (paged) */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {visible.map((w) => (
          <div
            key={w._id}
            style={{
              width: 280,
              backgroundColor: '#1e1e1e',
              padding: 20,
              borderRadius: 10,
              color: 'white',
            }}
          >
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1rem' }}>
              {w.name || 'Unnamed Writer'}
            </h3>
            <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.5 }}>
              {w.pro && (
                <div>
                  <strong>PRO:</strong> {w.pro}
                </div>
              )}
              {w.ipi && (
                <div>
                  <strong>IPI:</strong> {w.ipi}
                </div>
              )}
              {w.publisher && (
                <div>
                  <strong>Publisher:</strong> {w.publisher}
                </div>
              )}
            </div>

            <button
              onClick={() => openEditModal(w)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 10,
              }}
              title="Edit"
            >
              <img
                src={pencilIcon}
                alt="Edit"
                style={{
                  width: 20,
                  height: 20,
                  filter: 'brightness(0) saturate(100%) invert(20%)',
                }}
              />
              <span style={{ color: '#aaa', fontSize: 13 }}>Edit</span>
            </button>
          </div>
        ))}
      </div>

      {/* Load more */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        {visible.length < filtered.length && (
          <button
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            style={{
              padding: '10px 20px',
              background: '#333',
              border: 'none',
              color: '#fff',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}